import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation, useNavigate, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { getEffectiveRole, roleHome } from "@/lib/site";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import HowItWorks from "@/pages/HowItWorks";
import ForCreators from "@/pages/ForCreators";
import ForFans from "@/pages/ForFans";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Auth from "@/pages/Auth";
import Interests from "@/pages/Interests";
import Discover from "@/pages/Discover";
import CreatorProfile from "@/pages/CreatorProfile";
import FanDashboard from "@/pages/FanDashboard";
import Dashboard from "@/pages/Dashboard";
import PaymentSuccess from "@/pages/PaymentSuccess";
import AutoPilot from "@/pages/AutoPilot";
import Store from "@/pages/Store";
import Creators from "@/pages/Creators";
import Services from "@/pages/Services";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const Layout = () => (
  <div className="App bg-[#0a0612] min-h-screen">
    <Navbar />
    <main><Outlet /></main>
    <Footer />
  </div>
);

const Protected = ({ children }) => {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen" />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Hard role lock: only the matching role can view the page.
// Mismatch -> role's home; no role picked yet -> "/" where the RoleGate forces a pick.
const RoleRoute = ({ role, children }) => {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen" />;
  const eff = getEffectiveRole(user);
  if (eff !== role) return <Navigate to={eff ? roleHome(eff) : "/"} replace />;
  return children;
};

// Handles the Emergent Google OAuth return: exchanges session_id (URL fragment) for an app session
const AuthCallback = () => {
  const { googleAuth } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(loc.hash.slice(1)).get("session_id");
    const role = new URLSearchParams(loc.search).get("grole") || "fan";
    if (!sessionId) { nav("/login", { replace: true }); return; }
    googleAuth(sessionId, role)
      .then(({ user, is_new }) => {
        toast.success("Welcome to Fan Focus");
        window.history.replaceState(null, "", loc.pathname);
        if (user.type === "fan") nav(is_new ? "/interests" : "/fan/dashboard", { replace: true });
        else nav("/dashboard", { replace: true });
      })
      .catch(() => {
        toast.error("Google sign-in failed. Please try again.");
        nav("/login", { replace: true });
      });
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0612] flex items-center justify-center" data-testid="auth-callback">
      <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
    </div>
  );
};

const AppRoutes = () => {
  const location = useLocation();
  // Detect OAuth session_id synchronously during render (fragment, not query) — before any route/protected checks
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/for-creators" element={<RoleRoute role="creator"><ForCreators /></RoleRoute>} />
        <Route path="/for-fans" element={<RoleRoute role="fan"><ForFans /></RoleRoute>} />
        <Route path="/pricing" element={<RoleRoute role="creator"><Pricing /></RoleRoute>} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/discover" element={<RoleRoute role="fan"><Discover /></RoleRoute>} />
        {/* /@handle profile URLs — React Router v6 needs a full-segment param */}
        <Route path="/:handle" element={<CreatorProfile />} />
        <Route path="/login" element={<Auth />} />
        <Route path="/signup" element={<Auth />} />
        <Route path="/register" element={<Auth />} />
        <Route path="/interests" element={<Protected><RoleRoute role="fan"><Interests /></RoleRoute></Protected>} />
        <Route path="/dashboard" element={<Protected><RoleRoute role="creator"><Dashboard /></RoleRoute></Protected>} />
        <Route path="/fan/dashboard" element={<Protected><RoleRoute role="fan"><FanDashboard /></RoleRoute></Protected>} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        {/* legacy */}
        <Route path="/autopilot" element={<RoleRoute role="creator"><AutoPilot /></RoleRoute>} />
        <Route path="/services" element={<RoleRoute role="creator"><Services /></RoleRoute>} />
        <Route path="/creators" element={<RoleRoute role="creator"><Creators /></RoleRoute>} />
        <Route path="/store" element={<Store />} />
      </Route>
    </Routes>
  );
};

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AppRoutes />
        <Toaster position="top-right" theme="dark" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
